/**
 * Sydney verdict seed data.
 * Hand-seeded 20 starter neighborhoods — minimal viable scaffolding so
 * the city renders end-to-end. Same conservative approach as
 * verdicts-blr.ts: observable facts, "amber" by default, empty sources
 * until research lands.
 *
 * Schema is shared with verdicts.ts (HYD) — same `Verdict` interface.
 *
 * Australian price unit convention (matches verdicts-mel.ts):
 *   - Buy medians in A$k (so 1200 = A$1.2M)
 *   - Rent medians in $/wk
 * priceData is intentionally OMITTED until research lands; the band logic
 * falls through to a generic range when missing.
 */

import type { Verdict } from "./verdicts";
import { SYD_SUBURB_PRICING, getPricingFor } from "./suburb-pricing";

export const SYD_CENTER: [number, number] = [151.2093, -33.8688];

const _SYD_RAW: Verdict[] = [
  {
    id: "syd-cbd",
    name: "Sydney CBD",
    coord: [151.2093, -33.8688],
    verdict: "amber",
    reasons: [
      "Central business district — restaurants, retail, transport hub",
      "Train/light-rail/bus interchange at Town Hall + Central",
      "Limited residential family stock; mostly apartments + serviced",
    ],
    priceRange: "$900–$1,800 / wk rent",
    confidenceScore: 55,
    tags: ["cbd", "premium", "commercial-heavy"],
    sources: [],
  },
  {
    id: "syd-bondi",
    name: "Bondi",
    coord: [151.2735, -33.8916],
    verdict: "green",
    reasons: [
      "Beach-front lifestyle, dense café + restaurant strip",
      "Bondi Junction interchange — buses + Eastern Suburbs trains",
      "Premium pricing + summer crowding",
    ],
    priceRange: "A$1.4M+ apartment median",
    confidenceScore: 55,
    tags: ["beachside", "premium", "lifestyle"],
    sources: [],
  },
  {
    id: "syd-surry-hills",
    name: "Surry Hills",
    coord: [151.2113, -33.8845],
    verdict: "green",
    reasons: [
      "Inner-city village — terraces, restaurants, design studios",
      "Walkable to CBD + Central Station",
      "Mostly Victorian terrace stock; family floor-plates limited",
    ],
    priceRange: "A$1.6M+ terrace median",
    confidenceScore: 55,
    tags: ["inner-city", "premium", "young-professionals"],
    sources: [],
  },
  {
    id: "syd-newtown",
    name: "Newtown",
    coord: [151.1797, -33.8959],
    verdict: "green",
    reasons: [
      "King Street strip — bars, music venues, indie retail",
      "Sydney Uni adjacent; strong rental demand",
      "Inner-west character; some street noise on the strip",
    ],
    priceRange: "A$1.2M+ terrace median",
    confidenceScore: 55,
    tags: ["inner-west", "young-professionals", "lifestyle"],
    sources: [],
  },
  {
    id: "syd-manly",
    name: "Manly",
    coord: [151.2868, -33.7969],
    verdict: "green",
    reasons: [
      "Northern Beaches lifestyle — Manly ferry to Circular Quay",
      "Beach + harbour — high family demand",
      "Limited road access; ferry is the speed advantage",
    ],
    priceRange: "A$2M+ apartment median",
    confidenceScore: 55,
    tags: ["beachside", "northern-beaches", "premium", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-parramatta",
    name: "Parramatta",
    coord: [151.0010, -33.8136],
    verdict: "amber",
    reasons: [
      "Greater Sydney's second CBD — major office, retail, civic hub",
      "Train + light rail + Metro West (under construction)",
      "Apartment supply is heavy; tenant market in some sub-pockets",
    ],
    priceRange: "A$700k+ apartment median",
    confidenceScore: 55,
    tags: ["second-cbd", "western-sydney", "commercial-heavy"],
    sources: [],
  },
  {
    id: "syd-chatswood",
    name: "Chatswood",
    coord: [151.1827, -33.7969],
    verdict: "green",
    reasons: [
      "North Shore commercial hub — Westfield, Chatswood Chase",
      "Train + Metro North-West interchange",
      "High Asian-Australian community, strong food scene",
    ],
    priceRange: "A$1M+ apartment median",
    confidenceScore: 55,
    tags: ["north-shore", "commercial", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-mosman",
    name: "Mosman",
    coord: [151.2436, -33.8265],
    verdict: "green",
    reasons: [
      "Lower North Shore harbour-side — established, quiet, family",
      "Ferry + bus to CBD; no train station",
      "Premium price band — established stock, low turnover",
    ],
    priceRange: "A$3M+ house median",
    confidenceScore: 55,
    tags: ["north-shore", "premium", "family-friendly", "harbour-side"],
    sources: [],
  },
  {
    id: "syd-paddington",
    name: "Paddington",
    coord: [151.2271, -33.8848],
    verdict: "green",
    reasons: [
      "Heritage terrace streets — Oxford Street retail spine",
      "Walkable to CBD via Hyde Park (~25 min)",
      "Terrace stock — heritage controls limit redevelopment",
    ],
    priceRange: "A$2.2M+ terrace median",
    confidenceScore: 55,
    tags: ["inner-east", "premium", "heritage"],
    sources: [],
  },
  {
    id: "syd-glebe",
    name: "Glebe",
    coord: [151.1875, -33.8800],
    verdict: "green",
    reasons: [
      "Inner-west village — Glebe Point Road + Saturday markets",
      "Light rail to CBD + Sydney Uni walkable",
      "Mixed terrace + apartment stock; some heritage constraints",
    ],
    priceRange: "A$1.5M+ terrace median",
    confidenceScore: 50,
    tags: ["inner-west", "lifestyle", "heritage"],
    sources: [],
  },
  {
    id: "syd-pyrmont",
    name: "Pyrmont",
    coord: [151.1926, -33.8696],
    verdict: "amber",
    reasons: [
      "Harbour-side apartment district — close to CBD via Pyrmont Bridge",
      "Star casino + Darling Harbour entertainment adjacent",
      "Almost entirely apartment stock; school zoning limited",
    ],
    priceRange: "A$1.2M+ apartment median",
    confidenceScore: 50,
    tags: ["harbour-side", "apartment-heavy", "central"],
    sources: [],
  },
  {
    id: "syd-darlinghurst",
    name: "Darlinghurst",
    coord: [151.2174, -33.8794],
    verdict: "amber",
    reasons: [
      "Inner-east terrace + apartment mix — Oxford Street strip",
      "Late-night entertainment district; weekend noise",
      "Walkable to CBD, Surry Hills, Potts Point",
    ],
    priceRange: "A$1.3M+ apartment median",
    confidenceScore: 50,
    tags: ["inner-east", "lifestyle", "young-professionals"],
    sources: [],
  },
  {
    id: "syd-redfern",
    name: "Redfern",
    coord: [151.2046, -33.8927],
    verdict: "amber",
    reasons: [
      "Gentrifying inner-city — terrace stock + new-build apartments",
      "Redfern Station — single-stop to Central",
      "Mixed feel; pockets vary block-by-block",
    ],
    priceRange: "A$1.1M+ terrace median",
    confidenceScore: 50,
    tags: ["inner-city", "gentrifying", "transit-rich"],
    sources: [],
  },
  {
    id: "syd-marrickville",
    name: "Marrickville",
    coord: [151.1559, -33.9084],
    verdict: "green",
    reasons: [
      "Inner-west diversity — strong food scene, breweries, music",
      "Marrickville + Sydenham stations — direct to CBD",
      "Some apartment supply growth post-rezoning",
    ],
    priceRange: "A$1.3M+ house median",
    confidenceScore: 50,
    tags: ["inner-west", "lifestyle", "diverse"],
    sources: [],
  },
  {
    id: "syd-cronulla",
    name: "Cronulla",
    coord: [151.1517, -34.0573],
    verdict: "amber",
    reasons: [
      "Sutherland Shire beachside — train terminus from Bondi Junction",
      "Beach + cafe strip; family suburb feel",
      "Long commute to CBD (~50 min train)",
    ],
    priceRange: "A$1.6M+ house median",
    confidenceScore: 50,
    tags: ["beachside", "south-sydney", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-hornsby",
    name: "Hornsby",
    coord: [151.0986, -33.7037],
    verdict: "amber",
    reasons: [
      "Upper North Shore terminus — North Shore + Northern lines",
      "Family suburb; large catchment, public schools",
      "Commute distance is real (~40 min CBD)",
    ],
    priceRange: "A$1.4M+ house median",
    confidenceScore: 50,
    tags: ["north-shore", "family-friendly", "transit-rich"],
    sources: [],
  },
  {
    id: "syd-strathfield",
    name: "Strathfield",
    coord: [151.0859, -33.8786],
    verdict: "amber",
    reasons: [
      "Inner-west + western interchange — major rail hub",
      "Strong Korean-Australian community, food precincts",
      "Apartment supply heavy near station; older houses further out",
    ],
    priceRange: "A$1.5M+ house median",
    confidenceScore: 50,
    tags: ["inner-west", "transit-rich", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-liverpool",
    name: "Liverpool",
    coord: [150.9226, -33.9211],
    verdict: "amber",
    reasons: [
      "South-west growth corridor — proximity to Western Sydney Airport",
      "Liverpool train + bus interchange",
      "Lower price band; long commute to Sydney CBD",
    ],
    priceRange: "A$700k+ house median",
    confidenceScore: 50,
    tags: ["south-west", "growth-corridor", "affordable"],
    sources: [],
  },
  {
    id: "syd-penrith",
    name: "Penrith",
    coord: [150.6917, -33.7506],
    verdict: "amber",
    reasons: [
      "Far west — affordable end of greater Sydney",
      "Western Sydney Airport (under construction) adjacent",
      "Long commute to CBD (~60 min train); local jobs growing",
    ],
    priceRange: "A$650k+ house median",
    confidenceScore: 45,
    tags: ["far-west", "growth-corridor", "affordable"],
    sources: [],
  },
  {
    id: "syd-hurstville",
    name: "Hurstville",
    coord: [151.1009, -33.9670],
    verdict: "amber",
    reasons: [
      "St George region — train interchange + Westfield retail",
      "Strong Chinese-Australian community + food scene",
      "Apartment-heavy near station; older houses further out",
    ],
    priceRange: "A$1.1M+ house median",
    confidenceScore: 50,
    tags: ["south-sydney", "transit-rich", "family-friendly"],
    sources: [],
  },

  // ---- Phase-2 expansion (30 more well-known suburbs) ---------------
  // Hand-seeded so SYD has comparable density to MEL on launch. Same
  // conservative-verdict convention as Phase-1: green only where the
  // suburb is clearly established with diversified amenity; amber for
  // commute-dependent / car-only / single-axis suburbs. Coords from
  // OpenStreetMap suburb centroids.
  {
    id: "syd-kings-cross",
    name: "Kings Cross",
    coord: [151.2230, -33.8740],
    verdict: "amber",
    reasons: [
      "Inner-east — restaurants, late-night venues, Eastern Suburbs gateway",
      "Walking distance to CBD; Kings Cross station on T4 line",
      "Apartment-dominant, smaller footprints; nightlife noise on strips",
    ],
    priceRange: "A$900k+ unit median",
    confidenceScore: 50,
    tags: ["inner-east", "walkable", "transit-rich", "premium"],
    sources: [],
  },
  {
    id: "syd-double-bay",
    name: "Double Bay",
    coord: [151.2407, -33.8780],
    verdict: "green",
    reasons: [
      "Eastern Suburbs harbour village — cafés, boutiques, ferry wharf",
      "Walkable amenity stack; ferry to Circular Quay 12 min",
      "Premium house + apartment market — limited entry-level stock",
    ],
    priceRange: "A$3.5M+ house median",
    confidenceScore: 55,
    tags: ["eastern-suburbs", "premium", "harbour", "walkable"],
    sources: [],
  },
  {
    id: "syd-vaucluse",
    name: "Vaucluse",
    coord: [151.2790, -33.8580],
    verdict: "green",
    reasons: [
      "Premium Eastern Suburbs — clifftop views, harbour beaches",
      "Almost entirely standalone homes; very low apartment supply",
      "Car-dependent — limited bus service, no train",
    ],
    priceRange: "A$5M+ house median",
    confidenceScore: 55,
    tags: ["eastern-suburbs", "premium", "low-density", "harbour"],
    sources: [],
  },
  {
    id: "syd-woollahra",
    name: "Woollahra",
    coord: [151.2400, -33.8870],
    verdict: "green",
    reasons: [
      "Inner-east village — terrace homes, antique shops, pubs",
      "Walking distance to Centennial Park + Bondi Junction interchange",
      "Heritage stock keeps prices firm; tight rental market",
    ],
    priceRange: "A$3M+ house median",
    confidenceScore: 55,
    tags: ["inner-east", "premium", "walkable", "heritage"],
    sources: [],
  },
  {
    id: "syd-randwick",
    name: "Randwick",
    coord: [151.2438, -33.9145],
    verdict: "green",
    reasons: [
      "Eastern beaches inland — UNSW campus + Prince of Wales hospital",
      "Light rail (L2/L3) gives single-seat CBD commute",
      "Apartment + terrace mix; strong rental demand from students",
    ],
    priceRange: "A$2.5M+ house median",
    confidenceScore: 55,
    tags: ["eastern-suburbs", "transit-rich", "family-friendly", "education"],
    sources: [],
  },
  {
    id: "syd-coogee",
    name: "Coogee",
    coord: [151.2575, -33.9210],
    verdict: "green",
    reasons: [
      "Eastern beaches — bay swim, café strip, walking trail to Maroubra",
      "Bus-only transit to CBD (40 min); UNSW within walking distance",
      "Beach proximity premium; apartment-heavy near foreshore",
    ],
    priceRange: "A$2.8M+ house median",
    confidenceScore: 55,
    tags: ["beachside", "eastern-suburbs", "walkable"],
    sources: [],
  },
  {
    id: "syd-maroubra",
    name: "Maroubra",
    coord: [151.2440, -33.9460],
    verdict: "amber",
    reasons: [
      "Big surf beach — eastern suburbs, family + surf community",
      "Bus to CBD (~40–50 min); Light rail nearest at Kingsford",
      "Quieter than Coogee; apartment + house mix",
    ],
    priceRange: "A$2.4M+ house median",
    confidenceScore: 50,
    tags: ["beachside", "eastern-suburbs", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-kingsford",
    name: "Kingsford",
    coord: [151.2273, -33.9220],
    verdict: "amber",
    reasons: [
      "Adjacent to UNSW — high student-rental density",
      "Light rail terminus (L3) for CBD commute",
      "Café strip on Anzac Pde; mid-rise apartment dominant",
    ],
    priceRange: "A$2M+ house median",
    confidenceScore: 50,
    tags: ["eastern-suburbs", "transit-rich", "education"],
    sources: [],
  },
  {
    id: "syd-kensington",
    name: "Kensington",
    coord: [151.2250, -33.9120],
    verdict: "amber",
    reasons: [
      "Inner-east — UNSW + Royal Randwick racecourse + Centennial Park",
      "Light rail (L2) to CBD; apartment-heavy near campus",
      "Student-rental density limits family stock",
    ],
    priceRange: "A$2.3M+ house median",
    confidenceScore: 50,
    tags: ["eastern-suburbs", "transit-rich", "education"],
    sources: [],
  },
  {
    id: "syd-north-sydney",
    name: "North Sydney",
    coord: [151.2080, -33.8390],
    verdict: "green",
    reasons: [
      "North-shore CBD — major office tower cluster, restaurants",
      "T1 train + ferry; one stop from city across Harbour Bridge",
      "Apartment-dominant; limited family-stock at premium",
    ],
    priceRange: "A$1.4M+ unit median",
    confidenceScore: 55,
    tags: ["north-shore", "cbd-adjacent", "transit-rich"],
    sources: [],
  },
  {
    id: "syd-kirribilli",
    name: "Kirribilli",
    coord: [151.2147, -33.8470],
    verdict: "green",
    reasons: [
      "Harbour-side north-shore — heritage, opera house views, ferry",
      "Walking distance to Milsons Point train + North Sydney CBD",
      "Apartment-heavy; tight rental market, very few houses",
    ],
    priceRange: "A$1.6M+ unit median",
    confidenceScore: 55,
    tags: ["north-shore", "harbour", "premium", "walkable"],
    sources: [],
  },
  {
    id: "syd-neutral-bay",
    name: "Neutral Bay",
    coord: [151.2210, -33.8330],
    verdict: "green",
    reasons: [
      "Lower north-shore village — restaurants, ferry wharf, harbour walks",
      "Bus + ferry to CBD; train at Wynyard via Harbour Bridge buses",
      "Apartment + harbourside house mix; strong school stock",
    ],
    priceRange: "A$2.6M+ house median",
    confidenceScore: 55,
    tags: ["north-shore", "family-friendly", "harbour"],
    sources: [],
  },
  {
    id: "syd-lane-cove",
    name: "Lane Cove",
    coord: [151.1700, -33.8140],
    verdict: "green",
    reasons: [
      "Lower north-shore — bushland reserves, school catchments, village strip",
      "Bus-only to CBD; M2 motorway access for north-west jobs",
      "House-dominant; apartment growth near Epping Road tunnel",
    ],
    priceRange: "A$2.7M+ house median",
    confidenceScore: 55,
    tags: ["north-shore", "family-friendly", "schools-anchor"],
    sources: [],
  },
  {
    id: "syd-ryde",
    name: "Ryde",
    coord: [151.1043, -33.8127],
    verdict: "amber",
    reasons: [
      "Geographic centre of Sydney — Macquarie Park business district adjacent",
      "Metro Northwest line nearby (Macquarie Park / Macquarie University)",
      "Mix of older houses + new apartment density on Victoria Road",
    ],
    priceRange: "A$1.9M+ house median",
    confidenceScore: 50,
    tags: ["inner-north-west", "transit-rich", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-eastwood",
    name: "Eastwood",
    coord: [151.0810, -33.7910],
    verdict: "amber",
    reasons: [
      "North-west — train to CBD ~35 min; large Asian-Australian food scene",
      "Established house stock + new apartment infill near station",
      "Two distinct sides of station with different price bands",
    ],
    priceRange: "A$2.3M+ house median",
    confidenceScore: 50,
    tags: ["north-west", "transit-rich", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-burwood",
    name: "Burwood",
    coord: [151.1040, -33.8780],
    verdict: "amber",
    reasons: [
      "Inner-west transit hub — T1 + Westfield + light rail proposed",
      "High-density apartment growth; Burwood Road retail strip",
      "Construction churn around station precinct",
    ],
    priceRange: "A$1.1M+ unit median",
    confidenceScore: 50,
    tags: ["inner-west", "transit-rich", "high-density"],
    sources: [],
  },
  {
    id: "syd-balmain",
    name: "Balmain",
    coord: [151.1820, -33.8580],
    verdict: "green",
    reasons: [
      "Inner-west peninsula — heritage workers' cottages, harbourside pubs",
      "Ferry to Circular Quay; light rail to CBD via Pyrmont",
      "House-dominant heritage stock; very tight family demand",
    ],
    priceRange: "A$2.4M+ house median",
    confidenceScore: 55,
    tags: ["inner-west", "harbour", "heritage", "premium"],
    sources: [],
  },
  {
    id: "syd-rozelle",
    name: "Rozelle",
    coord: [151.1700, -33.8620],
    verdict: "green",
    reasons: [
      "Inner-west — adjacent to Balmain, light rail spur, weekend markets",
      "Walking distance to Bay Run + harbour; pubs + cafés on Darling St",
      "Heritage + warehouse-conversion stock; new metro under construction",
    ],
    priceRange: "A$2.2M+ house median",
    confidenceScore: 55,
    tags: ["inner-west", "harbour", "walkable"],
    sources: [],
  },
  {
    id: "syd-leichhardt",
    name: "Leichhardt",
    coord: [151.1540, -33.8830],
    verdict: "green",
    reasons: [
      "Inner-west Italian-heritage village — Norton St strip, Italian Forum",
      "Light rail + bus to CBD; future metro at Pyrmont nearby",
      "Terrace-dominant heritage stock; strong family demand",
    ],
    priceRange: "A$2M+ house median",
    confidenceScore: 55,
    tags: ["inner-west", "heritage", "family-friendly", "walkable"],
    sources: [],
  },
  {
    id: "syd-ashfield",
    name: "Ashfield",
    coord: [151.1264, -33.8870],
    verdict: "amber",
    reasons: [
      "Inner-west transit hub — T2/T3/T8 train interchange",
      "Strong Chinese + Sri Lankan food scene on Liverpool Road",
      "Apartment-dominant growth around station; older Federation houses inland",
    ],
    priceRange: "A$1.6M+ house median",
    confidenceScore: 50,
    tags: ["inner-west", "transit-rich", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-bankstown",
    name: "Bankstown",
    coord: [151.0350, -33.9170],
    verdict: "amber",
    reasons: [
      "South-west regional centre — train + Westfield + airport",
      "Affordable end of inner-south-west; apartment growth near station",
      "Future metro line under construction will improve CBD commute",
    ],
    priceRange: "A$1.1M+ house median",
    confidenceScore: 45,
    tags: ["south-west", "transit-rich", "affordable"],
    sources: [],
  },
  {
    id: "syd-blacktown",
    name: "Blacktown",
    coord: [150.9075, -33.7710],
    verdict: "amber",
    reasons: [
      "Western Sydney regional centre — train + Westfield + medical hub",
      "Affordable house market vs east + inner-west",
      "CBD commute 45–60 min train; western Sydney jobs growing",
    ],
    priceRange: "A$900k+ house median",
    confidenceScore: 45,
    tags: ["western-sydney", "transit-rich", "affordable"],
    sources: [],
  },
  {
    id: "syd-castle-hill",
    name: "Castle Hill",
    coord: [150.9930, -33.7320],
    verdict: "green",
    reasons: [
      "North-west — Sydney Metro line + Castle Towers retail anchor",
      "Family-stock dominant; strong school catchments",
      "Car-friendly with M2 / Norwest connection",
    ],
    priceRange: "A$2M+ house median",
    confidenceScore: 55,
    tags: ["north-west", "transit-rich", "family-friendly", "schools-anchor"],
    sources: [],
  },
  {
    id: "syd-baulkham-hills",
    name: "Baulkham Hills",
    coord: [150.9920, -33.7600],
    verdict: "green",
    reasons: [
      "Hills district — single-family homes, parks, school catchments",
      "Norwest business park nearby; bus + Metro connections",
      "Car-dependent for daily errands; quiet residential streets",
    ],
    priceRange: "A$1.8M+ house median",
    confidenceScore: 55,
    tags: ["north-west", "family-friendly", "schools-anchor"],
    sources: [],
  },
  {
    id: "syd-st-leonards",
    name: "St Leonards",
    coord: [151.1960, -33.8230],
    verdict: "green",
    reasons: [
      "North-shore commercial node — RNS Hospital + tower clusters",
      "T1 train + planned future metro upgrade",
      "Apartment-dominant; entry to Lower North Shore at lower price",
    ],
    priceRange: "A$1.2M+ unit median",
    confidenceScore: 55,
    tags: ["north-shore", "cbd-adjacent", "transit-rich"],
    sources: [],
  },
  {
    id: "syd-crows-nest",
    name: "Crows Nest",
    coord: [151.2020, -33.8260],
    verdict: "green",
    reasons: [
      "Lower north-shore village — restaurant + bar strip on Willoughby Rd",
      "Bus + future Sydney Metro West station planned",
      "Apartment + Federation home mix; young-professional demand",
    ],
    priceRange: "A$2.4M+ house median",
    confidenceScore: 55,
    tags: ["north-shore", "walkable", "young-professionals"],
    sources: [],
  },
  {
    id: "syd-killara",
    name: "Killara",
    coord: [151.1670, -33.7660],
    verdict: "green",
    reasons: [
      "Upper-north-shore — heritage homes, premium school catchments",
      "T1 train station; CBD commute ~25 min",
      "Strict heritage + tree zoning preserves leafy character",
    ],
    priceRange: "A$3.4M+ house median",
    confidenceScore: 55,
    tags: ["upper-north-shore", "premium", "schools-anchor"],
    sources: [],
  },
  {
    id: "syd-lindfield",
    name: "Lindfield",
    coord: [151.1700, -33.7780],
    verdict: "green",
    reasons: [
      "Upper-north-shore — leafy character, North Shore Line train",
      "Premium school catchment (Killara HS, Roseville College)",
      "House-dominant heritage stock; very tight family demand",
    ],
    priceRange: "A$3.2M+ house median",
    confidenceScore: 55,
    tags: ["upper-north-shore", "premium", "schools-anchor", "family-friendly"],
    sources: [],
  },
  {
    id: "syd-gordon",
    name: "Gordon",
    coord: [151.1540, -33.7560],
    verdict: "green",
    reasons: [
      "Ku-ring-gai centre — train + small village on Pacific Hwy",
      "Premium school catchments + bushland reserves nearby",
      "Heritage Federation homes — limited apartment infill",
    ],
    priceRange: "A$2.9M+ house median",
    confidenceScore: 55,
    tags: ["upper-north-shore", "family-friendly", "schools-anchor"],
    sources: [],
  },
  {
    id: "syd-wahroonga",
    name: "Wahroonga",
    coord: [151.1170, -33.7180],
    verdict: "green",
    reasons: [
      "Far upper-north-shore — Adventist hospital + grammar school catchments",
      "T1 train, M1 motorway access",
      "Large lots, leafy streets; car-dependent for non-rail trips",
    ],
    priceRange: "A$2.8M+ house median",
    confidenceScore: 55,
    tags: ["upper-north-shore", "premium", "family-friendly", "schools-anchor"],
    sources: [],
  },
];

/**
 * Public SYD verdicts — same records, priceData + investorEvidence
 * back-filled from the SYD pricing bank. Suburbs absent from the bank
 * fall through to band-only pricing (still works, just less precise).
 */
export const SYD_VERDICTS: Verdict[] = _SYD_RAW.map((v) => {
  const bank = getPricingFor(v.id);
  if (!bank) return v;
  return {
    ...v,
    priceData: v.priceData ?? bank.priceData,
    investorEvidence: v.investorEvidence ?? bank.investorEvidence,
  };
});
// Suppress "unused import" warning — SYD_SUBURB_PRICING is re-exported for
// any tooling that wants the raw bank without the override-applied helper.
void SYD_SUBURB_PRICING;

/**
 * Sydney-specific nearest-verdict lookup. Mirrors `nearestVerdict` /
 * `nearestVerdictMel` shape; no shared implementation per the per-city
 * duplication convention.
 */
export function nearestVerdictSyd(
  lng: number,
  lat: number,
  maxKm = 3,
): Verdict | null {
  let best: Verdict | null = null;
  let bestKm = Infinity;
  for (const v of SYD_VERDICTS) {
    const km = haversineKm(lat, lng, v.coord[1], v.coord[0]);
    if (km < bestKm) {
      bestKm = km;
      best = v;
    }
  }
  return bestKm <= maxKm ? best : null;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
